import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Boxes, CheckCircle2, MapPin, PackageCheck, Printer, RefreshCw, ScanLine, Tag } from "lucide-react";
import { api, API, apiError } from "@/lib/api";
import { fmtDate, STATUS_STYLES } from "@/lib/format";
import { BarcodeScanner } from "@/components/BarcodeScanner";
import { ABtn, ASelect, Panel } from "./AdminLayout";

export default function AdminPicking() {
  const [orders, setOrders] = useState(null);
  const [active, setActive] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [zones, setZones] = useState([]);
  const [scope, setScope] = useState("all");
  const [zoneId, setZoneId] = useState("");
  const [selected, setSelected] = useState([]);

  const load = async () => {
    try {
      const [{ data }, z] = await Promise.all([api.get("/warehouse/picking"), api.get("/delivery-zones")]);
      setOrders(data);
      setZones(z.data);
      setActive((prev) => (prev ? data.find((o) => o.id === prev.id) || null : null));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const ready = (orders || []).filter((o) => o.status === "Prête pour livraison");

  const printLabels = () => {
    const params = new URLSearchParams();
    if (selected.length) selected.forEach((id) => params.append("ids", id));
    else {
      params.set("scope", scope);
      if (zoneId) params.set("zone_id", zoneId);
    }
    window.open(`${API}/warehouse/labels?${params.toString()}`, "_blank");
  };

  const toggle = async (order, line) => {
    const next = line.picked
      ? order.lines.filter((l) => l.picked && l.item_id !== line.item_id).map((l) => l.item_id)
      : [...order.lines.filter((l) => l.picked).map((l) => l.item_id), line.item_id];
    try {
      await api.put(`/warehouse/picking/${order.id}`, { picked_item_ids: next });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const markReady = async (order) => {
    try {
      await api.post(`/warehouse/picking/${order.id}/ready`);
      toast.success(`Colis ${order.number} prêt pour livraison`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const grouped = useMemo(() => {
    if (!active) return [];
    const map = new Map();
    active.lines.forEach((l) => {
      if (!map.has(l.location)) map.set(l.location, []);
      map.get(l.location).push(l);
    });
    return [...map.entries()];
  }, [active]);

  if (!orders) return <p className="text-sm text-slate-400">Chargement des préparations…</p>;

  return (
    <div className="space-y-4" data-testid="admin-picking-page">
      <Panel
        title="Étiquettes colis en lot"
        subtitle={`${ready.length} colis prêt(s) pour livraison · format 10×15 cm, 1 étiquette par page`}
        actions={
          <>
            <ASelect value={scope} onChange={(e) => setScope(e.target.value)} className="w-52" data-testid="labels-scope-select">
              <option value="all">Tous les colis prêts</option>
              <option value="today">Colis prêts du jour</option>
            </ASelect>
            <ASelect value={zoneId} onChange={(e) => setZoneId(e.target.value)} className="w-52" data-testid="labels-zone-select">
              <option value="">Toutes les zones</option>
              {zones.map((z) => (
                <option key={z.id} value={z.id}>{z.name}</option>
              ))}
            </ASelect>
            <ABtn onClick={printLabels} disabled={ready.length === 0} data-testid="labels-print-button">
              <Tag className="h-3.5 w-3.5" />
              {selected.length ? `Imprimer ${selected.length} étiquette(s)` : "Imprimer les étiquettes"}
            </ABtn>
          </>
        }
        testid="labels-batch-panel"
      >
        <p className="text-xs text-slate-400" data-testid="labels-batch-hint">
          {selected.length
            ? `${selected.length} colis sélectionné(s) manuellement : les filtres sont ignorés.`
            : "Cochez des colis dans la file de préparation pour n'imprimer qu'une sélection, sinon les filtres ci-dessus s'appliquent."}
        </p>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-[1fr_1.5fr]">
        <Panel
          title="Commandes à préparer"
          subtitle={`${orders.length} colis en file d'attente`}
          actions={<ABtn variant="ghost" onClick={load} data-testid="picking-refresh"><RefreshCw className="h-3.5 w-3.5" /> Rafraîchir</ABtn>}
          testid="picking-queue-panel"
        >
          <div className="space-y-2">
            {orders.length === 0 && <p className="text-sm text-slate-500">Aucune commande à préparer.</p>}
            {orders.map((o) => (
              <div
                key={o.id}
                role="button"
                tabIndex={0}
                onClick={() => setActive(o)}
                onKeyDown={(e) => e.key === "Enter" && setActive(o)}
                className={`w-full cursor-pointer rounded-xl border p-3 text-left transition-colors ${
                  active?.id === o.id ? "border-gold bg-amber-950/20" : "border-slate-800 hover:bg-slate-800/40"
                }`}
                data-testid={`picking-order-${o.number}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    {o.status === "Prête pour livraison" && (
                      <input
                        type="checkbox"
                        checked={selected.includes(o.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) =>
                          setSelected((s) => (e.target.checked ? [...s, o.id] : s.filter((x) => x !== o.id)))
                        }
                        className="h-4 w-4 accent-gold"
                        data-testid={`labels-select-${o.number}`}
                      />
                    )}
                    <span className="font-num text-sm font-bold text-white">{o.number}</span>
                  </span>
                  <span className={`ds-chip ${STATUS_STYLES[o.status] || ""}`}>{o.status}</span>
                </div>
                <p className="mt-1 text-[11px] text-slate-400">
                  {o.customer_name} · {o.zone_name} · {fmtDate(o.created_at)}
                </p>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-800">
                  <div
                    className="h-full rounded-full bg-emerald-500 transition-all"
                    style={{ width: `${o.total_lines ? (o.picked_count / o.total_lines) * 100 : 0}%` }}
                  />
                </div>
                <p className="font-num mt-1 text-[11px] text-slate-400">
                  {o.picked_count}/{o.total_lines} article(s) préparé(s)
                </p>
              </div>
            ))}
          </div>
        </Panel>

        <Panel
          title={active ? `Préparation ${active.number}` : "Sélectionnez une commande"}
          subtitle={active ? `${active.customer_name} · ${active.phone} · ${active.zone_name}` : "Liste triée par emplacement d'entrepôt"}
          actions={
            active && (
              <>
                <ABtn variant="gold" onClick={() => setScanning(true)} data-testid="picking-scan-button">
                  <ScanLine className="h-3.5 w-3.5" /> Scanner
                </ABtn>
                <ABtn variant="ghost" onClick={() => window.open(`${API}/orders/${active.id}/label`, "_blank")} data-testid="picking-label-button">
                  <Tag className="h-3.5 w-3.5" /> Étiquette
                </ABtn>
                <ABtn variant="ghost" onClick={() => window.open(`/commande/${active.id}/bon-de-livraison`, "_blank")} data-testid="picking-receipt-button">
                  <Printer className="h-3.5 w-3.5" /> Bon
                </ABtn>
                <ABtn
                  onClick={() => markReady(active)}
                  disabled={active.picked_count < active.total_lines}
                  data-testid="picking-ready-button"
                >
                  <PackageCheck className="h-3.5 w-3.5" /> Colis prêt
                </ABtn>
              </>
            )
          }
          testid="picking-detail-panel"
        >
          {!active ? (
            <p className="text-sm text-slate-500">Choisissez un colis à gauche pour afficher sa liste de préparation.</p>
          ) : (
            <div className="space-y-4">
              {grouped.map(([location, lines]) => (
                <div key={location}>
                  <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gold">
                    <MapPin className="h-3.5 w-3.5" /> {location}
                  </p>
                  <div className="mt-1.5 space-y-1.5">
                    {lines.map((l) => (
                      <label
                        key={l.item_id}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${
                          l.picked ? "border-emerald-800 bg-emerald-950/30" : "border-slate-800 hover:bg-slate-800/40"
                        }`}
                        data-testid={`picking-line-${l.sku}`}
                      >
                        <input
                          type="checkbox"
                          checked={l.picked}
                          onChange={() => toggle(active, l)}
                          className="h-5 w-5 shrink-0 accent-emerald-500"
                          data-testid={`picking-check-${l.sku}`}
                        />
                        <span className="min-w-0 flex-1">
                          <span className={`block text-sm font-semibold ${l.picked ? "text-emerald-300 line-through" : "text-white"}`}>
                            {l.product_name}
                          </span>
                          <span className="font-num block text-[11px] text-slate-400">
                            {l.sku}
                            {l.barcode ? ` · ${l.barcode}` : ""}
                            {l.owner_type === "vendor" ? ` · vendeur ${l.owner_name}` : ""}
                            {` · stock ${l.stock}`}
                          </span>
                        </span>
                        <span className="font-num shrink-0 rounded-lg bg-slate-800 px-2.5 py-1 text-sm font-bold text-white">
                          × {l.quantity}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              <div className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950 p-3 text-xs text-slate-400">
                {active.picked_count === active.total_lines ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    Tous les articles sont préparés : cliquez sur « Colis prêt » pour passer la commande en « Prête pour livraison ».
                  </>
                ) : (
                  <>
                    <Boxes className="h-4 w-4 text-gold" />
                    Cochez chaque article prélevé. Le colis ne peut être marqué prêt qu'une fois la liste complète.
                  </>
                )}
              </div>
            </div>
          )}

          {scanning && active && (
            <BarcodeScanner
              onClose={() => setScanning(false)}
              onProduct={(p) => {
                const line = active.lines.find((l) => l.product_id === p.id);
                if (!line) {
                  toast.error(`${p.name} ne fait pas partie de cette commande`);
                  return;
                }
                if (line.picked) {
                  toast.info(`${p.name} est déjà coché`);
                  return;
                }
                toggle(active, line);
                toast.success(`${p.name} coché par scan`);
              }}
            />
          )}
        </Panel>
      </div>
    </div>
  );
}
